import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Avatar,
  Card,
  CardContent,
  Tabs,
  Tab,
  Snackbar,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  CircularProgress,
  Divider,
  FormHelperText,
  Chip,
  Fade,
  Grow,
  Container,
  Badge,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/styles';
import { Alert, Skeleton } from '@material-ui/lab';
import { 
  CameraAlt as CameraAltIcon,
  Save as SaveIcon,
  Security as SecurityIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  VerifiedUser as VerifiedUserIcon,
  Shield as ShieldIcon,
  Visibility,
  VisibilityOff,
  PhotoCamera,
  CloudUpload
} from '@material-ui/icons';
import { useAuth } from '../../../contexts/AuthContext';
import apiConfig from '../../../api/apiConfig';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';

// Styles using makeStyles (MUI v4)
const useStyles = makeStyles((theme) => ({
  styledContainer: {
    minHeight: '100vh',
    backgroundColor: '#12192c',
    paddingBottom: theme.spacing(4),
  },
  styledPaper: {
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
    backgroundColor: '#1a2235',
    color: '#ffffff',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    border: '1px solid #2d3748',
  },
  styledCard: {
    backgroundColor: '#1e293b',
    color: '#ffffff',
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
    border: '1px solid #2d3748',
    overflow: 'visible',
  },
  headerCard: {
    background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
    color: 'white',
    borderRadius: theme.spacing(2),
    marginBottom: theme.spacing(4),
    boxShadow: '0 10px 30px rgba(6, 182, 212, 0.3)',
  },
  styledButton: {
    background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
    color: 'white',
    padding: theme.spacing(1.5, 4),
    borderRadius: theme.spacing(3),
    textTransform: 'none',
    fontSize: '1rem',
    fontWeight: 500,
    boxShadow: '0 4px 15px rgba(6, 182, 212, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
      boxShadow: '0 6px 20px rgba(6, 182, 212, 0.4)',
      transform: 'translateY(-2px)',
    },
    '&:disabled': {
      background: '#4a5568',
      boxShadow: 'none',
    },
  },
  styledAvatar: {
    width: 120,
    height: 120,
    fontSize: '3rem',
    background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
    boxShadow: '0 8px 20px rgba(6, 182, 212, 0.3)',
    border: '4px solid #1e293b',
  },
  uploadOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.3s ease',
    cursor: 'pointer',
    '&:hover': {
      opacity: 1,
    },
  },
}));

// Custom styled components using withStyles
const StyledTextField = withStyles((theme) => ({
  root: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: theme.spacing(1),
      '& fieldset': {
        borderColor: '#2d3748',
      },
      '&:hover fieldset': {
        borderColor: '#4a5568',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#06b6d4',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#a0aec0',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#06b6d4',
    },
    '& .MuiOutlinedInput-input': {
      color: '#e2e8f0',
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
      color: '#06b6d4',
    },
    '& .MuiFormHelperText-root': {
      color: '#a0aec0',
    },
    '& .MuiFormHelperText-root.Mui-error': {
      color: '#f56565',
    },
  },
}))(TextField);

const StyledFormControl = withStyles((theme) => ({
  root: {
    '& .MuiInputLabel-root': {
      color: '#a0aec0',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#06b6d4',
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      '& fieldset': {
        borderColor: '#2d3748',
      },
      '&:hover fieldset': {
        borderColor: '#4a5568',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#06b6d4',
      },
    },
    '& .MuiSelect-select': {
      color: '#e2e8f0',
    },
    '& .MuiSvgIcon-root': {
      color: '#a0aec0',
    },
  },
}))(FormControl);

const StyledTabs = withStyles((theme) => ({
  root: {
    '& .MuiTabs-indicator': {
      height: 3,
      borderRadius: 3,
      backgroundColor: '#06b6d4',
    },
  },
}))(Tabs);

const StyledTab = withStyles((theme) => ({
  root: {
    textTransform: 'none',
    fontSize: '1rem',
    fontWeight: 500,
    minHeight: 64,
    color: '#a0aec0',
    '&.Mui-selected': {
      color: '#06b6d4',
    },
  },
}))(Tab);

export default function AdminProfileSettings() {
  const classes = useStyles();
  const [tabValue, setTabValue] = useState(0);
  const { user, updateUserData } = useAuth();
  const [message, setMessage] = useState({ text: '', type: '' });
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(true);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const isMounted = useRef(true);
  const { isCollapse } = useCollapseDrawer();
  const DRAWER_WIDTH = 280;
  const COLLAPSE_WIDTH = 102;

  // Form validation state
  const [errors, setErrors] = useState({
    Name: '',
    email: '',
    phone: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [profileData, setProfileData] = useState({
    Name: '',
    email: '',
    phone: '',
    role: '',
    permissions: [],
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    avatar: '',
  });

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        setFormLoading(true);
        const response = await apiConfig.fetchWithAuth('/users/profile');
        
        if (isMounted.current && response) {
          setProfileData((prev) => ({
            ...prev,
            Name: response.Name || '',
            email: response.email || '',
            phone: response.phone || '',
            role: response.role || 'admin',
            permissions: response.permissions || [],
            avatar: response.avatar || '',
          }));
          
          if (response.avatar) {
            setAvatarPreview(response.avatar);
          }
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        if (isMounted.current) {
          setMessage({ text: 'Error fetching admin data', type: 'error' });
        }
      } finally {
        if (isMounted.current) {
          setFormLoading(false);
        }
      }
    };

    fetchAdminData();
  }, []);

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 25;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
    if (/\d/.test(password) && /[^a-zA-Z\d]/.test(password)) strength += 25;
    return strength;
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    if (name === 'newPassword') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
    
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setMessage({ text: 'Image size should be less than 2MB', type: 'error' });
        return;
      }
      
      if (!file.type.match('image.*')) {
        setMessage({ text: 'Please select an image file', type: 'error' });
        return;
      }
      
      setAvatarFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validatePersonalInfo = () => {
    const newErrors = {};
    let isValid = true;
    
    if (!profileData.Name.trim()) {
      newErrors.Name = 'Name is required';
      isValid = false;
    }
    
    if (!profileData.email) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(profileData.email)) {
      newErrors.email = 'Email is invalid';
      isValid = false;
    }
    
    if (profileData.phone && !/^[0-9+\-\s()]{7,15}$/.test(profileData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
      isValid = false;
    }
    
    setErrors((prev) => ({ ...prev, ...newErrors }));
    return isValid;
  };

  const validatePasswordUpdate = () => {
    const newErrors = {};
    let isValid = true;
    
    if (!profileData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
      isValid = false;
    }
    
    if (!profileData.newPassword) {
      newErrors.newPassword = 'New password is required';
      isValid = false;
    } else if (profileData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
      isValid = false;
    }
    
    if (profileData.newPassword !== profileData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }
    
    setErrors((prev) => ({ ...prev, ...newErrors }));
    return isValid;
  };

  const handleProfileUpdate = async (event) => {
    event.preventDefault();
    
    if (!validatePersonalInfo()) {
      return;
    }
    
    try {
      setLoading(true);
      
      let response;
      
      if (avatarFile) {
        const formData = new FormData();
        formData.append('Name', profileData.Name);
        formData.append('email', profileData.email);
        formData.append('phone', profileData.phone);
        formData.append('avatar', avatarFile);
        
        response = await apiConfig.fetchWithAuth('/users/profile', {
          method: 'PUT',
          body: formData,
          headers: {}, 
        });
      } else {
        response = await apiConfig.fetchWithAuth('/users/profile', {
          method: 'PUT',
          body: JSON.stringify({
            Name: profileData.Name,
            email: profileData.email,
            phone: profileData.phone,
          }),
        });
      }

      if (isMounted.current && response) {
        setMessage({ text: 'Profile updated successfully', type: 'success' });
        if (updateUserData && typeof updateUserData === 'function') {
          updateUserData({
            ...user,
            Name: profileData.Name,
            email: profileData.email,
            phone: profileData.phone,
            avatar: response.avatar || profileData.avatar,
          });
        }
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      if (isMounted.current) {
        setMessage({ text: error.message || 'Error updating profile', type: 'error' });
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handlePasswordUpdate = async (event) => {
    event.preventDefault();
    
    if (!validatePasswordUpdate()) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await apiConfig.fetchWithAuth('/users/password', {
        method: 'PUT',
        body: JSON.stringify({
          currentPassword: profileData.currentPassword,
          newPassword: profileData.newPassword,
        }),
      });

      if (isMounted.current && response) {
        setMessage({ text: 'Password updated successfully', type: 'success' });
        setProfileData((prev) => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        }));
        setPasswordStrength(0);
      }
    } catch (error) {
      console.error('Error updating password:', error);
      if (isMounted.current) {
        setMessage({ 
          text: error.response?.data?.message || error.message || 'Error updating password', 
          type: 'error' 
        });
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleRoleUpdate = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const response = await apiConfig.fetchWithAuth('/users/role', {
        method: 'PUT',
        body: JSON.stringify({
          role: profileData.role,
          permissions: profileData.permissions,
        }),
      });

      if (isMounted.current && response) {
        setMessage({ text: 'Admin settings updated successfully', type: 'success' });
      }
    } catch (error) {
      console.error('Error updating admin settings:', error);
      if (isMounted.current) {
        setMessage({ text: error.message || 'Error updating admin settings', type: 'error' });
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength <= 25) return '#f44336';
    if (passwordStrength <= 50) return '#ff9800';
    if (passwordStrength <= 75) return '#ffc107';
    return '#4caf50';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength <= 25) return 'Weak';
    if (passwordStrength <= 50) return 'Fair';
    if (passwordStrength <= 75) return 'Good';
    return 'Strong';
  };

  const containerStyle = {
    marginLeft: isCollapse ? `${COLLAPSE_WIDTH + 24}px` : `${DRAWER_WIDTH + 24}px`,
    marginRight: '24px',
    transition: 'all 0.3s ease',
    paddingTop: '100px',
    paddingBottom: '50px',
  };

  if (window.innerWidth <= 1024) {
    containerStyle.marginLeft = '24px';
  }

  if (formLoading) {
    return (
      <Box className={classes.styledContainer} style={containerStyle}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {[...Array(6)].map((_, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Skeleton 
                  variant="rect" 
                  height={200} 
                  style={{ backgroundColor: '#2d3748', borderRadius: 16 }} 
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  return (
    <Box className={classes.styledContainer} style={containerStyle}>
      <Container maxWidth="lg">
        <Snackbar
          open={!!message.text}
          autoHideDuration={6000}
          onClose={() => setMessage({ text: '', type: '' })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            severity={message.type}
            onClose={() => setMessage({ text: '', type: '' })}
            elevation={6}
            variant="filled"
            style={{
              backgroundColor: message.type === 'success' ? '#06b6d4' : '#f56565',
            }}
          >
            {message.text}
          </Alert>
        </Snackbar>

        <Fade in timeout={800}>
          <Box>
            {/* Header Section */}
            <Card className={classes.headerCard}>
              <CardContent style={{ padding: 32 }}>
                <Typography variant="h3" style={{ fontWeight: 700, marginBottom: 8 }}>
                  Admin Profile
                </Typography>
                <Typography variant="h6" style={{ opacity: 0.9 }}>
                  Manage your account settings and administrative preferences
                </Typography>
              </CardContent>
            </Card>

            {/* Profile Overview Card */}
            <Grow in timeout={1000}>
              <Card className={classes.styledCard} style={{ marginBottom: 32 }}>
                <CardContent style={{ padding: 32 }}>
                  <Grid container spacing={4} alignItems="center">
                    <Grid item>
                      <Box style={{ position: 'relative' }}>
                        <Badge
                          overlap="circle"
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                          badgeContent={
                            <Tooltip title="Change avatar">
                              <label htmlFor="avatar-upload">
                                <input
                                  id="avatar-upload"
                                  type="file"
                                  accept="image/*"
                                  style={{ display: 'none' }}
                                  onChange={handleAvatarChange}
                                />
                                <IconButton 
                                  component="span"
                                  style={{
                                    backgroundColor: '#06b6d4',
                                    color: 'white',
                                    transition: 'all 0.2s ease',
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                                  }}
                                  size="small"
                                >
                                  <PhotoCamera />
                                </IconButton>
                              </label>
                            </Tooltip>
                          }
                        >
                          <Avatar 
                            className={classes.styledAvatar}
                            src={avatarPreview}
                            alt={profileData.Name}
                          >
                            {!avatarPreview && profileData.Name ? 
                              profileData.Name.charAt(0).toUpperCase() : 'A'}
                          </Avatar>
                        </Badge>
                      </Box>
                    </Grid>
                    <Grid item xs>
                      <Typography variant="h4" style={{ fontWeight: 600, marginBottom: 16, color: '#e2e8f0' }}>
                        {profileData.Name || 'Admin User'}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <EmailIcon style={{ fontSize: 20, color: '#06b6d4' }} />
                            <Typography variant="body1" style={{ color: '#a0aec0' }}>
                              {profileData.email}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <PhoneIcon style={{ fontSize: 20, color: '#06b6d4' }} />
                            <Typography variant="body1" style={{ color: '#a0aec0' }}>
                              {profileData.phone || 'No phone number'}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <ShieldIcon style={{ fontSize: 20, color: '#06b6d4' }} />
                            <Typography variant="body1" style={{ color: '#a0aec0' }}>
                              Role:
                            </Typography>
                            <Chip 
                              label={profileData.role ? profileData.role.charAt(0).toUpperCase() + 
                                profileData.role.slice(1) : 'Admin'} 
                              style={{
                                background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                                color: 'white',
                                fontWeight: 500,
                              }}
                              size="small"
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grow>

            {/* Tabs Section */}
            <Paper style={{ backgroundColor: 'transparent', marginBottom: 24, boxShadow: 'none' }}>
              <StyledTabs 
                value={tabValue} 
                onChange={handleTabChange}
                variant="fullWidth"
                style={{
                  backgroundColor: '#1e293b',
                  borderRadius: 16,
                  border: '1px solid #2d3748',
                }}
              >
                <StyledTab 
                  icon={<PersonIcon />} 
                  label="Personal Information" 
                />
                <StyledTab 
                  icon={<SecurityIcon />} 
                  label="Security" 
                />
                <StyledTab 
                  icon={<AdminIcon />} 
                  label="Admin Settings" 
                />
              </StyledTabs>
            </Paper>

            {/* Tab Content */}
            <Fade in key={tabValue} timeout={500}>
              <div>
                {tabValue === 0 && (
                  <Paper className={classes.styledPaper}>
                    <Typography variant="h5" style={{ marginBottom: 24, color: '#06b6d4', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <EditIcon /> Personal Information
                    </Typography>
                    <form onSubmit={handleProfileUpdate}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <StyledTextField
                            fullWidth
                            label="Full Name"
                            name="Name"
                            value={profileData.Name}
                            onChange={handleInputChange}
                            error={!!errors.Name}
                            helperText={errors.Name}
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PersonIcon style={{ color: '#06b6d4' }} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <StyledTextField
                            fullWidth
                            label="Email"
                            name="email"
                            type="email"
                            value={profileData.email}
                            onChange={handleInputChange}
                            error={!!errors.email}
                            helperText={errors.email}
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <EmailIcon style={{ color: '#06b6d4' }} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <StyledTextField
                            fullWidth
                            label="Phone Number"
                            name="phone"
                            value={profileData.phone}
                            onChange={handleInputChange}
                            error={!!errors.phone}
                            helperText={errors.phone}
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PhoneIcon style={{ color: '#06b6d4' }} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button 
                              type="submit"
                              className={classes.styledButton}
                              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                              disabled={loading}
                              size="large"
                            >
                              {loading ? 'Saving Changes...' : 'Save Changes'}
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </form>
                  </Paper>
                )}

                {tabValue === 1 && (
                  <Paper className={classes.styledPaper}>
                    <Typography variant="h5" style={{ marginBottom: 24, color: '#06b6d4', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <LockIcon /> Security Settings
                    </Typography>
                    <form onSubmit={handlePasswordUpdate}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <StyledTextField
                            fullWidth
                            label="Current Password"
                            name="currentPassword"
                            type={showPassword.current ? 'text' : 'password'}
                            value={profileData.currentPassword}
                            onChange={handleInputChange}
                            error={!!errors.currentPassword}
                            helperText={errors.currentPassword}
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LockIcon style={{ color: '#06b6d4' }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => setShowPassword(prev => ({ ...prev, current: !prev.current }))}
                                    edge="end"
                                    style={{ color: '#a0aec0' }}
                                  >
                                    {showPassword.current ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <StyledTextField
                            fullWidth
                            label="New Password"
                            name="newPassword"
                            type={showPassword.new ? 'text' : 'password'}
                            value={profileData.newPassword}
                            onChange={handleInputChange}
                            error={!!errors.newPassword}
                            helperText={errors.newPassword}
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LockOpenIcon style={{ color: '#06b6d4' }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => setShowPassword(prev => ({ ...prev, new: !prev.new }))}
                                    edge="end"
                                    style={{ color: '#a0aec0' }}
                                  >
                                    {showPassword.new ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                          {profileData.newPassword && (
                            <Box style={{ marginTop: 16 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={passwordStrength} 
                                style={{ 
                                  height: 8,
                                  borderRadius: 4,
                                  backgroundColor: '#2d3748',
                                }}
                                classes={{
                                  bar: {
                                    backgroundColor: getPasswordStrengthColor(),
                                    borderRadius: 4,
                                  }
                                }}
                              />
                              <Typography 
                                variant="caption" 
                                style={{ 
                                  color: getPasswordStrengthColor(),
                                  marginTop: 4,
                                  display: 'block'
                                }}
                              >
                                Password Strength: {getPasswordStrengthText()}
                              </Typography>
                            </Box>
                          )}
                        </Grid>
                        <Grid item xs={12}>
                          <StyledTextField
                            fullWidth
                            label="Confirm New Password"
                            name="confirmPassword"
                            type={showPassword.confirm ? 'text' : 'password'}
                            value={profileData.confirmPassword}
                            onChange={handleInputChange}
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword}
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LockIcon style={{ color: '#06b6d4' }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => setShowPassword(prev => ({ ...prev, confirm: !prev.confirm }))}
                                    edge="end"
                                    style={{ color: '#a0aec0' }}
                                  >
                                    {showPassword.confirm ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                  {profileData.confirmPassword && (
                                    profileData.newPassword === profileData.confirmPassword ? 
                                      <CheckIcon style={{ color: '#22c55e', marginLeft: 8 }} /> : 
                                      <CloseIcon style={{ color: '#f56565', marginLeft: 8 }} />
                                  )}
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Card variant="outlined" style={{ padding: 16, backgroundColor: 'rgba(6, 182, 212, 0.05)', border: '1px solid rgba(6, 182, 212, 0.2)' }}>
                            <Typography variant="subtitle2" style={{ marginBottom: 16, color: '#06b6d4' }}>
                              Password Requirements
                            </Typography>
                            <List dense>
                              <ListItem>
                                <ListItemIcon>
                                  <CheckIcon fontSize="small" style={{ color: '#06b6d4' }} />
                                </ListItemIcon>
                                <ListItemText 
                                  primary="At least 8 characters long" 
                                  primaryTypographyProps={{ variant: 'body2', style: { color: '#a0aec0' } }}
                                />
                              </ListItem>
                              <ListItem>
                                <ListItemIcon>
                                  <CheckIcon fontSize="small" style={{ color: '#06b6d4' }} />
                                </ListItemIcon>
                                <ListItemText 
                                  primary="Include uppercase and lowercase letters" 
                                  primaryTypographyProps={{ variant: 'body2', style: { color: '#a0aec0' } }}
                                />
                              </ListItem>
                              <ListItem>
                                <ListItemIcon>
                                  <CheckIcon fontSize="small" style={{ color: '#06b6d4' }} />
                                </ListItemIcon>
                                <ListItemText 
                                  primary="Add numbers and special characters" 
                                  primaryTypographyProps={{ variant: 'body2', style: { color: '#a0aec0' } }}
                                />
                              </ListItem>
                            </List>
                          </Card>
                        </Grid>
                        <Grid item xs={12}>
                          <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button 
                              type="submit"
                              className={classes.styledButton}
                              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SecurityIcon />}
                              disabled={loading || passwordStrength < 50}
                              size="large"
                            >
                              {loading ? 'Updating Password...' : 'Update Password'}
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </form>
                  </Paper>
                )}

                {tabValue === 2 && (
                  <Paper className={classes.styledPaper}>
                    <Typography variant="h5" style={{ marginBottom: 24, color: '#06b6d4', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <VerifiedUserIcon /> Admin Settings
                    </Typography>
                    <form onSubmit={handleRoleUpdate}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <StyledFormControl fullWidth variant="outlined">
                            <InputLabel>Admin Role</InputLabel>
                            <Select
                              name="role"
                              value={profileData.role}
                              label="Admin Role"
                              onChange={handleInputChange}
                            >
                              <MenuItem value="admin">
                                <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <AdminIcon fontSize="small" />
                                  <span>Administrator</span>
                                </Box>
                              </MenuItem>
                              <MenuItem value="editor">
                                <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <EditIcon fontSize="small" />
                                  <span>Editor</span>
                                </Box>
                              </MenuItem>
                              <MenuItem value="viewer">
                                <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <PersonIcon fontSize="small" />
                                  <span>Viewer</span>
                                </Box>
                              </MenuItem>
                            </Select>
                          </StyledFormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box style={{ 
                            padding: 16, 
                            borderRadius: 16, 
                            backgroundColor: 'rgba(6, 182, 212, 0.05)',
                            border: '1px solid rgba(6, 182, 212, 0.2)'
                          }}>
                            <Typography variant="subtitle2" style={{ color: '#06b6d4', marginBottom: 8 }}>
                              Current Permissions
                            </Typography>
                            <Typography variant="body2" style={{ color: '#a0aec0' }}>
                              {profileData.permissions.length} permissions active
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" gutterBottom style={{ color: '#e2e8f0', fontWeight: 500 }}>
                            Permissions
                          </Typography>
                          <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {['view_courts', 'book_courts', 'manage_courts', 'view_transactions', 
                              'manage_users', 'admin_dashboard'].map((permission) => (
                              <Chip
                                key={permission}
                                label={permission.replace(/_/g, ' ').toUpperCase()}
                                icon={<VerifiedUserIcon />}
                                clickable
                                onClick={() => {
                                  const newPermissions = profileData.permissions.includes(permission)
                                    ? profileData.permissions.filter(p => p !== permission)
                                    : [...profileData.permissions, permission];
                                  setProfileData({ ...profileData, permissions: newPermissions });
                                }}
                                color={profileData.permissions.includes(permission) ? "primary" : "default"}
                                variant={profileData.permissions.includes(permission) ? "default" : "outlined"}
                                style={{
                                  backgroundColor: profileData.permissions.includes(permission) 
                                    ? 'rgba(6, 182, 212, 0.8)' 
                                    : 'rgba(255, 255, 255, 0.05)',
                                  color: profileData.permissions.includes(permission) ? '#ffffff' : '#a0aec0',
                                  borderColor: profileData.permissions.includes(permission) ? '#06b6d4' : '#4a5568',
                                }}
                              />
                            ))}
                          </Box>
                          <FormHelperText style={{ color: '#a0aec0', marginTop: 8 }}>
                            Click on permissions to toggle them on or off
                          </FormHelperText>
                        </Grid>
                        <Grid item xs={12}>
                          <Box style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 16
                          }}>
                            <Typography variant="body2" style={{ color: '#a0aec0' }}>
                              Selected permissions: {profileData.permissions.length}
                            </Typography>
                            <Button 
                              type="submit"
                              className={classes.styledButton}
                              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AdminIcon />}
                              disabled={loading}
                              size="large"
                            >
                              {loading ? 'Saving Settings...' : 'Save Admin Settings'}
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </form>
                  </Paper>
                )}
              </div>
            </Fade>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
}